// Game Types and Enums
export var GameType;
(function (GameType) {
    GameType["DEADPAN"] = "deadpan";
    GameType["UNDERCOVER"] = "undercover";
    GameType["BOMB_RELAY"] = "bomb_relay";
    GameType["PENSIEVE"] = "pensieve";
    GameType["SIMILAR_MINDS"] = "similar_minds";
    GameType["ACE_ATTORNEY"] = "ace_attorney";
    GameType["GAME_THEORY_WHEEL"] = "game_theory_wheel";
})(GameType || (GameType = {}));
export var GamePhase;
(function (GamePhase) {
    GamePhase["WAITING"] = "waiting";
    GamePhase["WORDSMITH_INPUT"] = "wordsmith_input";
    GamePhase["FACT_INPUT"] = "fact_input";
    GamePhase["ROLE_ASSIGNMENT"] = "role_assignment";
    GamePhase["VOTING"] = "voting";
    GamePhase["RESULTS"] = "results";
})(GamePhase || (GamePhase = {}));
export var PlayerRole;
(function (PlayerRole) {
    PlayerRole["DEADPAN"] = "deadpan";
    PlayerRole["FACT_SPEAKER"] = "fact_speaker";
    PlayerRole["DETECTIVE"] = "detective";
    PlayerRole["WORDSMITH"] = "wordsmith";
    PlayerRole["UNDERCOVER"] = "undercover";
    PlayerRole["CIVILIAN"] = "civilian";
    PlayerRole["BLANK"] = "blank"; // Blank - No word, needs to figure out the word
})(PlayerRole || (PlayerRole = {}));
export var DeadpanItemRarity;
(function (DeadpanItemRarity) {
    DeadpanItemRarity["NORMAL"] = "normal";
    DeadpanItemRarity["RARE"] = "rare";
    DeadpanItemRarity["EPIC"] = "epic";
})(DeadpanItemRarity || (DeadpanItemRarity = {}));
export var DeadpanItemId;
(function (DeadpanItemId) {
    DeadpanItemId["PREP_MINUS_FIVE"] = "prep_minus_five";
    DeadpanItemId["PREP_PLUS_FIVE"] = "prep_plus_five";
    DeadpanItemId["EXPLANATION_MINUS_FIVE"] = "explanation_minus_five";
    DeadpanItemId["USELESS_HINT"] = "useless_hint";
    DeadpanItemId["COINFLIP_CATEGORY"] = "coinflip_category";
    DeadpanItemId["CATEGORY_HINT"] = "category_hint";
    DeadpanItemId["REORDER_SPEAKERS"] = "reorder_speakers";
    DeadpanItemId["BIG_TIME_CUT"] = "big_time_cut";
})(DeadpanItemId || (DeadpanItemId = {}));
export var TimerPhase;
(function (TimerPhase) {
    TimerPhase["EXPLANATION_DISPLAY"] = "explanation_display";
    TimerPhase["PREPARATION"] = "preparation";
    TimerPhase["BOMB_RELAY"] = "bomb_relay";
    TimerPhase["SIMILAR_MINDS_ANSWER"] = "similar_minds_answer";
    TimerPhase["SIMILAR_MINDS_VOTING"] = "similar_minds_voting";
    TimerPhase["NONE"] = "none";
})(TimerPhase || (TimerPhase = {}));
// Voice (Socket ack; realtime backend is an implementation detail)
export const VOICE_TOKEN_ERROR = {
    NOT_IN_GAME_ROOM: 'NOT_IN_GAME_ROOM',
    ROOM_NOT_FOUND: 'ROOM_NOT_FOUND',
    VOICE_NOT_SET: 'VOICE_NOT_SET',
    INVALID_PASSWORD: 'INVALID_PASSWORD',
    VOICE_SERVER_NOT_CONFIGURED: 'VOICE_SERVER_NOT_CONFIGURED',
    INTERNAL: 'INTERNAL',
};
export const PENSIEVE_TOOLS_DRAW_ERROR = {
    NO_QUESTIONS: 'NO_QUESTIONS',
    INTERNAL: 'INTERNAL',
};
// Game Constants
export const MIN_PLAYERS = 3;
export const MIN_PLAYERS_SIMILAR_MINDS = 2;
export const MIN_PLAYERS_ACE_ATTORNEY = 2;
export const ACE_ATTORNEY_QUESTION_MAX = 300;
export const ACE_ATTORNEY_JUDGMENT_MAX = 500;
export const ACE_ATTORNEY_ANSWER_MAX = 300;
export const MAX_PLAYERS = 8;
export const MIN_PLAYERS_PENSIEVE = 2; // Host + at least one other to be respondent
export const MIN_PLAYERS_GAME_THEORY_WHEEL = 2;
export const MAX_PLAYERS_GAME_THEORY_WHEEL = 6;
export const MIN_PLAYERS_STAND_APART = 4;
export const MIN_PLAYERS_SCHRODINGER = 2;
export const STAND_APART_CARD_COUNT_MIN = 2;
export const STAND_APART_CARD_COUNT_MAX = 6;
export const STAND_APART_CARD_COUNT_DEFAULT = 4;
export const MIN_PLAYERS_FOR_WORDSMITH = 4; // Minimum players required to assign Wordsmith
export function clampStandApartCardCount(n) {
    if (!Number.isFinite(n)) {
        return STAND_APART_CARD_COUNT_DEFAULT;
    }
    return Math.min(STAND_APART_CARD_COUNT_MAX, Math.max(STAND_APART_CARD_COUNT_MIN, Math.round(n)));
}
export function getNashMode(room) {
    const mode = room.nashConfig?.mode;
    if (mode === 'stand_apart' || mode === 'schrodinger_croc') {
        return mode;
    }
    return 'wheel';
}
export function isNashStandApart(room) {
    return room.gameType === GameType.GAME_THEORY_WHEEL && getNashMode(room) === 'stand_apart';
}
export function isNashWheel(room) {
    return room.gameType === GameType.GAME_THEORY_WHEEL && getNashMode(room) === 'wheel';
}
export function isNashSchrodinger(room) {
    return room.gameType === GameType.GAME_THEORY_WHEEL && getNashMode(room) === 'schrodinger_croc';
}
export function nashPlayerLimits(mode) {
    if (mode === 'stand_apart') {
        return { min: MIN_PLAYERS_STAND_APART, max: MAX_PLAYERS };
    }
    if (mode === 'schrodinger_croc') {
        return { min: MIN_PLAYERS_SCHRODINGER, max: MAX_PLAYERS };
    }
    return { min: MIN_PLAYERS_GAME_THEORY_WHEEL, max: MAX_PLAYERS_GAME_THEORY_WHEEL };
}
export function getNashMinPlayers(room) {
    if (room.gameType !== GameType.GAME_THEORY_WHEEL) {
        return MIN_PLAYERS;
    }
    return nashPlayerLimits(getNashMode(room)).min;
}
export function getNashMaxPlayers(room) {
    if (room.gameType !== GameType.GAME_THEORY_WHEEL) {
        return MAX_PLAYERS;
    }
    return nashPlayerLimits(getNashMode(room)).max;
}
export const DEFAULT_MAX_ROUNDS = 5;
export const ROOM_CODE_LENGTH = 6;
// Export words (types only), translations, and i18n — no game content JSON
export * from './words';
export * from './i18n';
export * from './gameTheoryWheel';
export * from './schrodingerCroc';
//# sourceMappingURL=index.js.map